import React, { useState } from 'react';
import { registerUser } from '../../DatabaseAPI';
import { useAuth } from '../../auth/AuthContext';

function UserRegistration({ betaKey, onRegistered, onBack }) {
    // `betaKey` holds the live `key_code`; log presence only, never the value.
    console.log('UserRegistration: Rendering, betaKey present:', Boolean(betaKey));

    const { setAuthenticatedUser } = useAuth(); // Get AuthProvider methods
    
    const [formData, setFormData] = useState({
        email: betaKey?.target_email || '',
        password: '',
        confirmPassword: ''
    });
    
    // formData carries the user's email — log shape, not contents.
    console.log('UserRegistration: Initial form ready, email prefilled:', Boolean(formData.email));
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Basic validation
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (formData.password.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }

        setIsLoading(true);

        try {
            const response = await registerUser({
                email: formData.email,
                password: formData.password,
                beta_key: betaKey.key_code
            });

            if (response.status === 'success') {
                console.log('UserRegistration: Registration successful, updating AuthProvider...');
                
                // Since backend auto-logged in the user, immediately set them as authenticated
                setAuthenticatedUser(response.user);
                
                // Notify parent component (this will trigger navigation to Play Screen)
                onRegistered(response.user);
            } else {
                setError(response.message || 'Registration failed');
            }
        } catch (error) {
            console.error('Registration error:', error);
            setError('Registration failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={{ 
            maxWidth: '450px', 
            margin: '0 auto', 
            padding: '40px 20px',
            fontFamily: 'Arial, sans-serif'
        }}>
            <div style={{
                backgroundColor: '#2c3e50',
                color: 'white',
                padding: '20px',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
                <h2 style={{ 
                    textAlign: 'center', 
                    marginTop: '0',
                    marginBottom: '30px' 
                }}>
                    👤 Create Account
                </h2>

                {betaKey && (
                    <div style={{
                        backgroundColor: '#27ae60',
                        padding: '10px',
                        borderRadius: '4px',
                        marginBottom: '20px',
                        textAlign: 'center',
                        fontSize: '14px'
                    }}>
                        ✅ Beta key validated: {betaKey.key_code}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '20px' }}>
                        <label htmlFor="email" style={{ 
                            display: 'block', 
                            marginBottom: '8px',
                            fontWeight: 'bold'
                        }}>
                            Email Address:
                        </label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            disabled={isLoading || (betaKey && betaKey.key_type === 'INDIVIDUAL')}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '4px',
                                border: '1px solid #bdc3c7',
                                fontSize: '16px',
                                boxSizing: 'border-box',
                                backgroundColor: (betaKey && betaKey.key_type === 'INDIVIDUAL') ? '#ecf0f1' : 'white'
                            }}
                            placeholder="your.email@example.com"
                        />
                        {betaKey && betaKey.key_type === 'INDIVIDUAL' && (
                            <small style={{ color: '#95a5a6', fontSize: '12px' }}>
                                Email locked for individual beta key
                            </small>
                        )}
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label htmlFor="password" style={{ 
                            display: 'block', 
                            marginBottom: '8px',
                            fontWeight: 'bold'
                        }}>
                            Password:
                        </label>
                        <input
                            type="password"
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                            disabled={isLoading}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '4px',
                                border: '1px solid #bdc3c7',
                                fontSize: '16px',
                                boxSizing: 'border-box'
                            }}
                            placeholder="At least 8 characters"
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label htmlFor="confirmPassword" style={{ 
                            display: 'block', 
                            marginBottom: '8px',
                            fontWeight: 'bold'
                        }}>
                            Confirm Password:
                        </label>
                        <input
                            type="password"
                            id="confirmPassword"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            required
                            disabled={isLoading}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '4px',
                                border: '1px solid #bdc3c7',
                                fontSize: '16px',
                                boxSizing: 'border-box'
                            }}
                            placeholder="Re-enter password"
                        />
                    </div>

                    {error && (
                        <div style={{
                            backgroundColor: '#e74c3c',
                            color: 'white',
                            padding: '10px',
                            borderRadius: '4px',
                            marginBottom: '20px',
                            textAlign: 'center'
                        }}>
                            {error}
                        </div>
                    )}

                    <div style={{ textAlign: 'center' }}>
                        <button
                            type="submit"
                            disabled={isLoading || !formData.email || !formData.password || !formData.confirmPassword}
                            style={{
                                backgroundColor: isLoading ? '#95a5a6' : '#27ae60',
                                color: 'white',
                                padding: '12px 24px',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                marginRight: '10px'
                            }}
                        >
                            {isLoading ? 'Creating Account...' : 'Create Account'}
                        </button>

                        <button
                            type="button"
                            onClick={onBack}
                            disabled={isLoading}
                            style={{
                                backgroundColor: '#95a5a6',
                                color: 'white',
                                padding: '12px 24px',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                fontSize: '16px'
                            }}
                        >
                            ← Back
                        </button>
                    </div>
                </form>

                <div style={{ 
                    marginTop: '30px', 
                    padding: '15px',
                    backgroundColor: '#34495e',
                    borderRadius: '4px',
                    fontSize: '14px',
                    lineHeight: '1.4'
                }}>
                    <strong>🏈 Account Setup</strong><br/>
                    Create your Mythic Football account to join leagues and manage your fantasy teams.
                    Your beta key allows up to 10 simultaneous leagues.
                </div>
            </div>
        </div>
    );
}

export default UserRegistration;